import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Writing and editing → Editor essentials.
// Fixture: `reviewTheDraft` (the finished Valley Mart report, opened directly).
const CASE_ID = "c0000000-0000-4000-8000-000000000201";
const NARRATIVE = `${sel("editorNarrative")} .ProseMirror`;
const SIDE = (title: string) => `${sel("editorSidebarTabs")} button[title="${title}"]`;

const scene: Scene = {
  id: "editor-essentials",
  title: "Editor essentials",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "reviewTheDraft",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Open any case from Cases and you're in the editor. Your narrative is on the left with a toolbar above it, and the report tools are in the panel on the right.",
      actions: [
        { type: "waitFor", target: sel("editorNarrative") },
        { type: "wait", ms: 2700 },
        { type: "moveTo", target: sel("editorNarrative") },
        { type: "spotlight", target: sel("editorNarrative") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: sel("editorSidebarTabs") },
        { type: "wait", ms: 2000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "toolbar",
      narration:
        "Across the top is the toolbar. The Styles dropdown switches between Paragraph and Headings, and beside it are Bold, Italic, Underline, lists, Undo, and Redo.",
      actions: [
        { type: "moveTo", target: sel("editorStyles") },
        { type: "wait", ms: 1300 },
        { type: "click", target: sel("editorStyles") },
        { type: "wait", ms: 2900 },
        { type: "click", target: sel("editorStyles") },
        { type: "spotlight", target: sel("editorToolbar") },
        { type: "wait", ms: 3500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "tools",
      narration:
        "Find and Replace searches the text. Writing suggestions turns on inline proofreading, and Transcribe Audio lets you dictate right at the cursor.",
      actions: [
        { type: "moveTo", target: sel("editorFind") },
        { type: "spotlight", target: sel("editorFind") },
        { type: "wait", ms: 1800 },
        { type: "moveTo", target: sel("editorProofread") },
        { type: "spotlight", target: sel("editorProofread") },
        { type: "wait", ms: 2600 },
        { type: "moveTo", target: sel("editorTranscribe") },
        { type: "spotlight", target: sel("editorTranscribe") },
        { type: "wait", ms: 2000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "organize",
      narration:
        "At the right end are a category tag and a favorite star for organizing the case, and then Export.",
      actions: [
        { type: "moveTo", target: sel("caseCategoryTag") },
        { type: "spotlight", target: sel("caseCategoryTag") },
        { type: "wait", ms: 1500 },
        { type: "moveTo", target: sel("favoriteStar") },
        { type: "spotlight", target: sel("favoriteStar") },
        { type: "wait", ms: 1800 },
        { type: "moveTo", target: sel("editorExport") },
        { type: "spotlight", target: sel("editorExport") },
        { type: "wait", ms: 1500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "tabs",
      narration:
        "Above the text, Narrative and Transcription switch your view. A Footage tab appears here when the case has analyzed video.",
      actions: [
        { type: "moveTo", target: sel("editorTabTranscription") },
        { type: "wait", ms: 1500 },
        { type: "click", target: sel("editorTabTranscription") },
        { type: "wait", ms: 3200 },
        { type: "click", target: sel("editorTabNarrative") },
        { type: "wait", ms: 1200 },
      ],
    },
    {
      id: "panel",
      narration:
        "In the right panel, Suggestions holds the score and gaps. Snippets holds boilerplate, Statutes holds charges, and Redactions marks personal information. A Checklist tab joins them when a case started from a checklist.",
      actions: [
        // Timed to the clip: Snippets 4.1 s, Statutes 6.0 s, Redactions 7.9 s,
        // back to Suggestions as the Checklist sentence starts at 10.6 s.
        { type: "moveTo", target: SIDE("Snippets") },
        { type: "wait", ms: 3000 },
        { type: "click", target: SIDE("Snippets") },
        { type: "wait", ms: 1500 },
        { type: "click", target: SIDE("Statutes") },
        { type: "wait", ms: 1300 },
        { type: "click", target: SIDE("Redactions") },
        { type: "wait", ms: 2400 },
        { type: "click", target: SIDE("Suggestions") },
        { type: "wait", ms: 1000 },
      ],
    },
    {
      id: "selection",
      narration:
        "Select any text in the narrative and a floating menu opens, with Enhance Writing, Redact, and Unredact.",
      actions: [
        { type: "moveTo", target: NARRATIVE },
        { type: "wait", ms: 1500 },
        { type: "selectText", target: NARRATIVE, text: "I photographed the recovered merchandise and returned it to the store" },
        { type: "wait", ms: 800 },
        { type: "waitFor", target: sel("enhanceWriting") },
        { type: "spotlight", target: sel("enhanceWriting") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "saving",
      narration:
        "The editor saves as you type, but only while it has focus. If you dictate or paste from another window, click into the narrative once before you close the tab.",
      actions: [
        { type: "selectText", target: NARRATIVE, text: "I photographed the recovered merchandise and returned it to the store", collapse: "end" },
        { type: "spotlight", target: sel("editorNarrative") },
        { type: "wait", ms: 7000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
